<template>
  <div>
    <navbar :links="links" />
    <!-- Header Text -->
    <v-layout class="mt-4">
      <span class="grey--text text--darken-2 subheading">{{
        $t('devicesHint')
      }}</span>
    </v-layout>
    <v-alert
      v-if="wifiCommissioningAvailable && redirectedFromWifiSetupPage"
      value="true"
      type="info"
      class="mt-3"
    >
      {{ $t('setupWifiInfoAlertLabel') }}!
    </v-alert>
    <!-- Wifi Commissioning Button for mobile -->
    <v-slide-x-reverse-transition>
      <div v-if="$vuetify.breakpoint.xs && wifiCommissioningAvailable">
        <v-btn
          block
          class="msr mt-3"
          round
          dark
          @click="startWifiCommissioning()"
        >
          <v-icon left>
            wifi
          </v-icon>
          {{ $t('setupWifiOnNewPlayer') }}
        </v-btn>
        <div
          v-if="!probablyInAppWrapper"
          class="text-xs-center mt-3 mb-1"
        >
          <router-link :to="{name: 'wifisetup'}">
            {{ $t('setupWifiPageLink') }}
          </router-link>
        </div>
      </div>
    </v-slide-x-reverse-transition>
    <v-dialog
      v-model="wifiCommissioningDemoUserDialogOpen"
      transition="dialog-bottom-transition"
      lazy
      width="400"
    >
      <v-card>
        <v-card-title>
          <v-layout align-center>
            <div class="headline">
              {{ $t('setupWifiOnNewPlayer') }}
            </div>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <p>
            {{ $t('setupWifiDemoUserWarning') }}
          </p>
          <p>
            {{
              $t('setupWifiDemoUserExpiryWarning', {
                days: userProfileExpiresWithinDays,
              })
            }}
          </p>
          <p>
            {{ $t('setupWifiDemoUserAdminInfo') }}
          </p>
        </v-card-text>
        <v-card-actions align-right>
          <v-spacer />
          <btn-abort
            round
            :label="$t('cancel')"
            @click="wifiCommissioningDemoUserDialogOpen = false"
          />
          <btn-ok
            round
            :label="$t('setupWifiContinueCommissioning')"
            @click="confirmStartWifiComissioning()"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <!-- Mobile card-based UI -->
    <div v-if="$vuetify.breakpoint.xs">
      <div
        :class="{
          ...$desktopModeStyles,
          'elevation-1': scrollTop > fixedSearchThreshold,
          tools: scrollTop <= fixedSearchThreshold,
          'tools-fixed': scrollTop > fixedSearchThreshold,
        }"
      >
        <!-- Mobile Search -->
        <v-text-field
          v-if="!loading"
          v-model="debounceddevicesSearchText"
          style="max-width: 400px"
          hide-details
          :label="searchLabel"
          clearable
          append-icon="search"
          @click:clear="devicesSearchText = ''"
          @keyup.native.esc="devicesSearchText = ''"
        />
      </div>
      <device-card
        v-for="(device, deviceIndex) in mobileSortedFilteredAugmentedDevices"
        :key="'device-card-' + deviceIndex"
        :device="device"
        :headers="headers"
        :is-slave="device.is_slave"
        class="my-2 elevation-5"
        @renamed="fetchDevices({indicator: false})"
      />
    </div>
    <!-- Desktop table-based UI -->
    <div v-else>
      <v-layout
        v-if="!loading"
        align-center
        justify-center
        class="mt-4 mb-4"
      >
        <v-layout
          wrap
          justify-center
          align-center
        >
          <!-- Reset Table Sorting -->
          <v-slide-x-transition>
            <v-btn
              v-if="showResetDevicesTableSortingButton"
              small
              flat
              round
              color="msr"
              class="mr-3"
              :title="$t('devicesResetSortingButtonTooltip')"
              @click="resetDevicesTableSorting()"
            >
              <v-icon left>
                sort
              </v-icon>
              {{ $t('devicesResetSortingButtonLabel') }}
            </v-btn>
          </v-slide-x-transition>
          <!-- Desktop Search -->
          <v-text-field
            v-model="debounceddevicesSearchText"
            class="pt-0 my-0"
            style="max-width: 400px"
            hide-details
            :label="searchLabel"
            clearable
            append-icon="search"
            @click:clear="devicesSearchText = ''"
            @keyup.native.esc="devicesSearchText = ''"
          />
          <!-- Refresh list -->
          <v-btn
            flat
            class="white grey--text text--darken-1 ml-2"
            round
            icon
            :title="$t('refresh')"
            @click="refreshDevicesList()"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
          <v-slide-x-reverse-transition>
            <v-btn
              v-if="wifiCommissioningAvailable"
              class="msr"
              dark
              round
              @click="startWifiCommissioning()"
            >
              <v-icon left>
                wifi
              </v-icon>
              {{ $t('setupWifiOnNewPlayer') }}
            </v-btn>
          </v-slide-x-reverse-transition>
          <!-- Export to Excel file -->
          <v-btn
            v-if="devices.length > 0"
            flat
            class="white grey--text text--darken-1"
            fixed
            right
            top
            round
            style="z-index: 3; top: 56px"
            @click="exportDevicesTableToFile()"
          >
            <v-icon left>
              insert_drive_file
            </v-icon>
            {{ $t('exportToFile') }}
          </v-btn>
        </v-layout>
      </v-layout>
      <!-- Devices Table -->
      <v-data-table
        v-if="!loading"
        ref="table"
        :custom-filter="customTableSearchFilter"
        :headers="headers"
        :items="augmentedDevices"
        :no-data-text="$t('noDevicesYet')"
        :no-results-text="$t('noDevicesFoundForSearch')"
        :pagination.sync="devicesPagination"
        :search="devicesSearchText"
        hide-actions
        must-sort
      >
        <template
          slot="items"
          slot-scope="props"
        >
          <device-site
            v-if="
              isGroupedByStore &&
                deviceIdentToShowStoreGroupingHeader[props.item.ident]
            "
            :device="props.item"
            class="mt-3 mb-2"
          />
          <device-table-row
            :device="props.item"
            :is-slave="props.item.is_slave"
            :border="
              !isGroupedByStore &&
                !deviceIdentToShowStoreGroupingHeader[props.item.ident]
            "
            :show-site="!isGroupedByStore"
            @renamed="fetchDevices({indicator: false})"
          />
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import orderBy from 'lodash.orderby';

import {importXlsx} from '@/deferredImports';
import matchesFilter from '@/matchesFilter';

import {getDeviceStatusSearchableText} from '@/mixins/deviceStatus.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import DeviceCard from '@/components/Device/DeviceCard';
import DeviceSite from '@/components/Device/DeviceSite';
import DeviceTableRow from '@/components/Device/DeviceTableRow';
import Navbar from '@/components/Navbar';
import BtnOk from '@/components/ButtonOk';
import BtnAbort from '@/components/ButtonAbort';

const DEFAULT_TABLE_SORT_BY = 'site_sortkey';

export default {
  components: {
    DeviceCard,
    DeviceSite,
    DeviceTableRow,
    Navbar,
    BtnOk,
    BtnAbort,
  },
  mixins: [trackScrollTop],
  data() {
    return {
      xlsx: null,

      links: ['dashboard', 'devices'],
      loading: false,
      filteredDevices: [],

      // See trackScrollTop mixin.
      fixedSearchThreshold: 176,

      // Based on optional window.MYSHOPRADIO_APP_WRAPPER namespace,
      // which is not reactive and injected as a new script tag after
      // document has loaded.
      wifiCommissioningAvailable: false,
      wifiCommissioningDemoUserDialogOpen: false,
    };
  },
  computed: {
    redirectedFromWifiSetupPage() {
      return (
        this.$route &&
        this.$route.query &&
        this.$route.query.wifisetup == 'true'
      );
    },
    probablyInAppWrapper() {
      return (
        this.$store.state.common.runtimeEnv.android ||
        this.$store.state.common.runtimeEnv.iOS
      );
    },
    deviceIdentToShowStoreGroupingHeader() {
      // Show grouping header only for first device of each Store.
      const storeIdToDeviceIdents = {};
      for (const device of this.filteredDevices) {
        if (!storeIdToDeviceIdents[device.store.id]) {
          storeIdToDeviceIdents[device.store.id] = [];
        }
        storeIdToDeviceIdents[device.store.id].push(device.ident);
      }
      const deviceIdentToShowHeader = {};
      for (const storeId of Object.keys(storeIdToDeviceIdents)) {
        const deviceIdents = storeIdToDeviceIdents[storeId];
        for (let i = 0; i < deviceIdents.length; i++) {
          const deviceIdent = deviceIdents[i];
          deviceIdentToShowHeader[deviceIdent] =
            i == 0 && this.isGroupedByStore;
        }
      }
      return deviceIdentToShowHeader;
    },
    isGroupedByStore() {
      return this.devicesPagination.sortBy === DEFAULT_TABLE_SORT_BY;
    },
    showResetDevicesTableSortingButton() {
      return this.$vuetify.breakpoint.smAndUp && !this.isGroupedByStore;
    },
    devicesSearchText: {
      get() {
        return this.$store.state.common.devicesSearchText;
      },
      set(text) {
        this.$store.commit('SET_DEVICES_SEARCH_TEXT', text || '');
      },
    },
    devicesPagination: {
      get() {
        return this.$store.state.common.devicesPagination;
      },
      set(pagination) {
        this.$store.commit('SET_DEVICES_PAGINATION', pagination);
      },
    },
    searchLabel() {
      let searchResults = this.augmentedDevices;
      if (this.devicesSearchText) {
        searchResults = this.filteredDevices;
      }
      return `${this.$t('search')} (${searchResults.length}) ...`;
    },
    debounceddevicesSearchText: {
      get() {
        return this.devicesSearchText;
      },
      set: debounce(function (text) {
        this.devicesSearchText = text;
      }, 250),
    },
    headers() {
      return [
        {
          text: this.$t('site'),
          align: 'left',
          value: 'site_sortkey',
        },
        {
          text: this.$t('decoderType'),
          align: 'left',
          value: 'decodertype',
        },
        {
          text: this.$t('description'),
          align: 'left',
          value: 'description',
        },
        {
          text: this.$t('deviceAddress'),
          align: 'left',
          value: 'deviceAddress',
        },
        {
          text: this.$t('status'),
          align: 'left',
          value: 'online_sortkey',
        },
        {
          text: this.$t('lastOnline'),
          align: 'left',
          value: 'last_online_sortkey',
        },
        {
          text: this.$t('actions'),
          align: 'left',
          value: 'actions',
          sortable: false,
        },
      ];
    },
    devices() {
      return this.$store.state.common.devices;
    },
    deviceIdentToDevice() {
      const identToDevice = {};
      for (const device of this.devices) {
        identToDevice[device.ident] = device;
      }
      return identToDevice;
    },
    deviceMasterIdentToDevices() {
      const masterIdentToDevices = {};
      for (const device of this.devices) {
        if (device.master_id) {
          const masterIdent = this.makeDecoderIdent(
            device.ctype,
            device.master_id
          );
          if (masterIdentToDevices[masterIdent] == undefined) {
            masterIdentToDevices[masterIdent] = [];
          }
          masterIdentToDevices[masterIdent].push(device);
        }
      }
      return masterIdentToDevices;
    },
    augmentedDevices() {
      return this.devices.map((device, index) => {
        device.index = index + 1;

        if (device.ctype !== 'tv') {
          device.content_updated = true;
        }

        device.decodertype = device.ctype;
        device.originalDecodertype = device.decodertype;
        if (device.tvformat) {
          device.decodertype =
            device.decodertype +
            '_' +
            device.tvformat.name +
            '_' +
            device.tvformat.id;
        }
        if (device.ctype == 'visitorcount_sensor') {
          device.decodertype += '_' + this.$t('visitorcount');
        }

        // Assemble default sortkey (store data first for proper grouping).
        device.site_sortkey =
          device.store.city +
          ' ' +
          device.store.name +
          ' ' +
          device.store.location +
          ' ' +
          device.store.address +
          ' ' +
          device.store.zipcode +
          ' ' +
          device.decodertype +
          ' ' +
          (device.master_id || 0) +
          ' ' +
          device.description;

        device.online_sortkey =
          '' +
          (device.first_activation ? 1 : 0) +
          (device.online ? 1 : 0) +
          (device.content_updated ? 1 : 0);

        device.last_online_sortkey =
          '' +
          (device.online && device.content_updated ? null : device.last_online);

        const ip = device.localip || device.wifiip;
        device.deviceAddress = device.mac + ' ' + (ip || '');
        device.isWifi = ip !== device.localip && ip === device.wifiip;
        device.ip = ip;

        if (device.master_id) {
          const masterIdent = this.makeDecoderIdent(
            device.ctype,
            device.master_id
          );
          device.master = this.deviceIdentToDevice[masterIdent];
        }

        // Add translated state messages so we can search for them.
        device.statusText = getDeviceStatusSearchableText(device).toLowerCase();

        return device;
      });
    },
    mobileFilteredAugmentedDevices() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return this.filteredDevices;
      }
      const noOp = () => null;
      return this.customTableSearchFilter(
        this.augmentedDevices,
        this.devicesSearchText,
        noOp
      );
    },
    mobileSortedFilteredAugmentedDevices() {
      return orderBy(this.mobileFilteredAugmentedDevices, 'site_sortkey');
    },
    userProfileExpiresWithinDays() {
      if (!this.$userprofile || !this.$userprofile.expiration_date) {
        return null;
      }
      var timeToExpiry =
        new Date(this.$userprofile.expiration_date) - new Date();
      return Math.ceil(timeToExpiry / (1000 * 60 * 60 * 24));
    },
  },
  watch: {
    wifiCommissioningAvailable: function (available, previousAvailable) {
      const nowAvailable = !previousAvailable && !!available;
      if (nowAvailable && this.redirectedFromWifiSetupPage) {
        // We are within a native app wrapper and have skipped the wifisetup
        // help page to get here, meaning we can start right away with the
        // setup.
        this.startWifiCommissioning();
      }
    },
  },
  created() {
    importXlsx().then((lib) => (this.xlsx = lib));

    this.$intervals.registerLongpollingCall(
      this.refreshDevicesList,
      this.$intervals.everyMinute
    );
  },
  beforeDestroy() {
    this.$intervals.unregisterLongpollingCall(this.refreshDevicesList);
  },
  methods: {
    checkWifiCommissioningAvailable() {
      this.wifiCommissioningAvailable =
        window.MYSHOPRADIO_APP_WRAPPER !== undefined &&
        window.MYSHOPRADIO_APP_WRAPPER.startWifiCommissioning !== undefined;
    },
    resetDevicesTableSorting() {
      this.$store.commit('RESET_DEVICES_PAGINATION');
    },
    startWifiCommissioning() {
      if (this.$userprofile.is_demo) {
        this.wifiCommissioningDemoUserDialogOpen = true;
      } else {
        this.confirmStartWifiComissioning();
      }
    },
    confirmStartWifiComissioning() {
      this.wifiCommissioningDemoUserDialogOpen = false;
      this.$api.trackWifiSetupStarted();
      window.MYSHOPRADIO_APP_WRAPPER.startWifiCommissioning();
    },
    refreshDevicesList() {
      this.fetchDevices({indicator: false});
    },
    exportDevicesTableToFile() {
      // Map header and rows to array-of-arrays which SheetJS understands.
      const headerRow = [
        this.$t('site'),
        this.$t('storeAssociationMemberNumber'),
        this.$t('decoderType'),
        this.$t('description'),
        this.$t('serial'),
        'MAC (LAN)',
        'MAC (WIFI)',
        'IP',
        this.$t('status'),
        this.$t('lastOnline'),
      ];

      // // Accessing table.filteredItems will give us the same sorting
      // // that the User sees and may have changed himself.
      const tableRows = this.$refs.table.filteredItems.map((row) => {
        return [
          // Site
          row.store.name +
            ', ' +
            row.store.city +
            ' ' +
            row.store.location +
            ' ' +
            row.store.zipcode +
            ' ' +
            row.store.address,
          // Store Association Membernumber
          row.store.association_membernumber,
          // Decodertype
          row.originalDecodertype +
            ' ' +
            (row.tvformat ? row.tvformat.name : ''),
          // Description
          row.description,
          // Serial/Player-ID
          row.serial,
          // MAC (LAN)
          row.mac,
          // MAC (WIFI)
          row.wifimac || '',
          // IP
          row.ip,
          // Status
          (row.online ? '' : this.$t('offline')) +
            ' ' +
            (!row.content_updated ? this.$t('contentIsNotUpToDate') : ''),
          // Last online
          row.last_online ? row.last_online : '',
        ];
      });
      const rowsToExport = [headerRow, ...tableRows];

      const workbook = this.xlsx.utils.book_new();
      const worksheet = this.xlsx.utils.aoa_to_sheet(rowsToExport);
      this.xlsx.utils.book_append_sheet(
        workbook,
        worksheet,
        this.$t('devices')
      );
      const basename = this.$t('devices');
      this.xlsx.writeFile(workbook, `${basename}.xlsx`);
    },
    makeDecoderIdent(ctype, id) {
      return ctype + 'decoder_' + id;
    },
    customTableSearchFilter(devices, search, filter) {
      search = search.toString().trim().toLowerCase();
      const words = search.split(' ');

      this.filteredDevices = devices.filter((device) => {
        return words.every((word) => {
          return (
            matchesFilter(device.decodertype, word) ||
            matchesFilter(device.description, word) ||
            matchesFilter(device.deviceAddress, word) ||
            matchesFilter(device.last_online, word) ||
            matchesFilter(device.store.address, word) ||
            matchesFilter(device.store.city, word) ||
            matchesFilter(device.store.location, word) ||
            matchesFilter(device.store.name, word) ||
            matchesFilter(device.store.association_membernumber, word) ||
            matchesFilter(device.store.zipcode, word) ||
            matchesFilter(device.statusText, word)
          );
        });
      });
      return this.filteredDevices;
    },
    fetchDevices({indicator = true} = {}) {
      if (indicator) {
        this.loading = true;
      }
      this.$store.dispatch('LIST_DEVICES').finally(() => {
        if (indicator) {
          this.loading = false;
        }
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // HACK: The state we are testing is set by the native app wrapper
      //   at some later point, to ensure it shows up initially we may
      //   need to delay our check.
      for (const seconds of [100, 300, 1000]) {
        setTimeout(vm.checkWifiCommissioningAvailable, seconds);
      }

      vm.fetchDevices({indicator: vm.devices.length === 0});
    });
  },
};
</script>

<style scoped>
.bg-grey-subtle {
  background-color: rgba(0, 0, 0, 0.04);
}

.limited-table {
  max-height: calc(100vh - 300px);
  overflow: auto;
}

.tools {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 18px;
}

.tools-fixed {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 40px;
  left: 0;
  width: 100%;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 8px;
  background-color: white;
  z-index: 2; /* Fix .v-chip__content overlapping with x-index of 1. */
}

.tools-fixed-spacer {
  margin-bottom: 70px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Devices.vue